import {
  Attributes,
  BudgetWithIds,
  type ItemRestriction,
  type Origin,
  Restrictions,
  type RnbCalculatorPayload,
  SellerOperator,
} from 'typings'
import { ApplyTo } from 'ui/components/PromotionForm/Restrictions/typings'

export type APIRestrictionsPayload = Pick<
  Partial<RnbCalculatorPayload>,
  | 'maxUsage'
  | 'maxUsagePerClient'
  | 'cumulative'
  | 'accumulateWithManualPrice'
  | 'idSeller'
  | 'idSellerIsInclusive'
  | 'idsSalesChannel'
  | 'areSalesChannelIdsExclusive'
  | 'origin'
>

export function toAPIMaxUsage(maxUsage: number | undefined): {
  maxUsage: RnbCalculatorPayload['maxUsage']
} {
  return {
    maxUsage: maxUsage ?? 0,
  }
}

export function toAPIMaxUsagePerClient(maxUsagePerClient: number | undefined): {
  maxUsagePerClient: RnbCalculatorPayload['maxUsagePerClient']
} {
  return {
    maxUsagePerClient: maxUsagePerClient ?? 0,
  }
}

export function toAPIMaxUsagePerCart(
  maxItemsPerCart: Restrictions['maxItemsPerCart']
): {
  maxNumberOfAffectedItems: RnbCalculatorPayload['maxNumberOfAffectedItems']
  isAppliedToMostExpensive?: RnbCalculatorPayload['isAppliedToMostExpensive']
} {
  if (!maxItemsPerCart)
    return {
      maxNumberOfAffectedItems: 0,
    }

  const { value, applyTo } = maxItemsPerCart
  if (!applyTo) {
    return {
      maxNumberOfAffectedItems: value,
    }
  }

  return {
    maxNumberOfAffectedItems: value,
    isAppliedToMostExpensive: applyTo === ApplyTo.mostExpensive,
  }
}

export function toAPIAccumulate(accumulate: boolean): {
  cumulative: RnbCalculatorPayload['cumulative']
} {
  return {
    cumulative: accumulate,
  }
}

export function toAPIAccumulateWithManualPrices(
  accumulateWithManualPrices: boolean | null
): {
  accumulateWithManualPrice: RnbCalculatorPayload['accumulateWithManualPrice']
} {
  return {
    accumulateWithManualPrice: accumulateWithManualPrices,
  }
}

export function toAPISellers(sellers: Restrictions['sellers']): {
  idSeller: RnbCalculatorPayload['idSeller']
  idSellerIsInclusive: RnbCalculatorPayload['idSellerIsInclusive']
  optIn: RnbCalculatorPayload['optIn']
} {
  if (sellers?.operator === SellerOperator.OPTIN) {
    return {
      idSeller: null,
      idSellerIsInclusive: null,
      optIn: { sellers: sellers.items.map((seller) => seller.id).reverse() },
    }
  }

  return {
    idSeller: sellers?.items.map((seller) => seller.id).join(',') ?? '',
    idSellerIsInclusive:
      sellers === undefined || sellers?.operator === SellerOperator.EQUALS,
    optIn: null,
  }
}

export function toAPISalesChannels(
  salesChannels: ItemRestriction | undefined
): {
  idsSalesChannel: RnbCalculatorPayload['idsSalesChannel']
  areSalesChannelIdsExclusive: RnbCalculatorPayload['areSalesChannelIdsExclusive']
} {
  return {
    idsSalesChannel: salesChannels?.items.map((sc) => sc.id) ?? [],
    areSalesChannelIdsExclusive:
      salesChannels === undefined || salesChannels?.operator === 'different',
  }
}

export function toAPIOrigin(origin: Origin): {
  origin: RnbCalculatorPayload['origin']
} {
  return {
    origin: origin,
  }
}

export function toAPIMaxItemsPerAttribute(
  maxItemsPerAttribute: Restrictions['maxItemsPerAttribute']
): Pick<
  Partial<RnbCalculatorPayload>,
  | 'maxNumberOfAffectedItems'
  | 'maxNumberOfAffectedItemsGroupKey'
  | 'isAppliedToMostExpensive'
> {
  if (!maxItemsPerAttribute)
    return {
      maxNumberOfAffectedItems: 0,
    }

  const groupKeyMap: Record<
    Attributes,
    RnbCalculatorPayload['maxNumberOfAffectedItemsGroupKey']
  > = {
    [Attributes.CART]: 'perCart',
    [Attributes.PRODUCT]: 'perProductId',
    [Attributes.SKU]: 'perSku',
  }

  return {
    maxNumberOfAffectedItems: maxItemsPerAttribute.value,
    maxNumberOfAffectedItemsGroupKey:
      groupKeyMap[maxItemsPerAttribute.attribute],
    isAppliedToMostExpensive:
      maxItemsPerAttribute.applyTo === ApplyTo.mostExpensive,
  }
}

export function toAPIBudget(
  budget: BudgetWithIds | undefined
): Pick<Partial<RnbCalculatorPayload>, 'budget'> {
  if (!budget)
    return {
      budget: undefined,
    }
  const { id, allocationId } = budget
  return {
    budget: {
      budgetId: id,
      allocationId,
    },
  }
}

export const restrictionsToAPIMap: Record<keyof Restrictions, Function> = {
  maxUsage: toAPIMaxUsage,
  maxUsagePerClient: toAPIMaxUsagePerClient,
  maxItemsPerCart: toAPIMaxUsagePerCart,
  maxItemsPerAttribute: toAPIMaxItemsPerAttribute,
  accumulate: toAPIAccumulate,
  accumulateWithManualPrices: toAPIAccumulateWithManualPrices,
  sellers: toAPISellers,
  salesChannels: toAPISalesChannels,
  origin: toAPIOrigin,
  budget: toAPIBudget,
}

export function toAPIRestrictions(
  restrictions: Partial<Restrictions>
): APIRestrictionsPayload {
  return Object.entries(restrictions)
    .filter((entries) => entries !== undefined)
    .reduce((acc, [key, value]) => {
      const toAPI = restrictionsToAPIMap[key]

      return {
        ...acc,
        ...toAPI(value),
      }
    }, {})
}
